import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Building, Wrench, ShieldCheck, Package, Truck, AlertCircle, CheckCircle, Mail, Info, Calendar, User, MapPin, History, ImageIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import styles from './EquipmentHistoryPanel.module.css';
import PhotoViewerModal from '../PhotoModal/PhotoViewerModal';
import NotesViewerModal from '../Equipment/NotesViewerModal';

const EquipmentHistoryPanel = ({
  isOpen,
  onClose,
  equipmentId,
  equipment = null, // Опциональные предзагруженные данные
  title = "История оборудования",
}) => {
  const [equipmentHistory, setEquipmentHistory] = useState(equipment);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [isNotesViewerOpen, setIsNotesViewerOpen] = useState(false);
  const [currentPhotos, setCurrentPhotos] = useState([]);
  const [currentNotes, setCurrentNotes] = useState([]);
  
  // Новый state для управления анимацией открытия/закрытия
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);

  useEffect(() => {
    if (isOpen && equipmentId && !equipmentHistory) {
      fetchEquipmentHistory();
    }
    if (isOpen) {
      // При открытии: небольшой delay для триггера transition после монтирования
      const timer = setTimeout(() => {
        setIsAnimatingOpen(true);
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isOpen, equipmentId, equipmentHistory]);

  useEffect(() => {
    if (equipment) {
      setEquipmentHistory(equipment);
    }
  }, [equipment]);

  const fetchEquipmentHistory = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get(`/repairs/equipments/${equipmentId}/`);
      setEquipmentHistory(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке истории:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка при загрузке истории оборудования';
      setError(errorMsg);
      toast.error(errorMsg, {
        toastId: 'history-error',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const closePanel = () => {
    // Сначала запускаем анимацию закрытия
    setIsAnimatingOpen(false);
    // После завершения transition (0.4s = 400ms) закрываем и чистим
    setTimeout(() => {
      if (typeof onClose === 'function') {
        onClose();
      }
      setEquipmentHistory(null);
      setError('');
      setCurrentPhotos([]);
      setCurrentNotes([]);
      setIsPhotoViewerOpen(false);
      setIsNotesViewerOpen(false);
    }, 400);
  };

  const handleClose = () => closePanel();

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).replace(',', '');
  };

  const calculateTotalCost = (repairCodes) => {
    if (!repairCodes || !Array.isArray(repairCodes)) return 0;
    return repairCodes.reduce((sum, rc) => {
      const price = parseFloat(rc.price) || 0;
      return sum + price;
    }, 0);
  };

  const getEquipmentLocation = (equipment) => {
    if (!equipment) return '-';
    
    if (equipment.location_status === 'awaiting_shipping' || equipment.location_status === 'shipped') {
      return equipment.packaging_box
        ? `${equipment.packaging_box.name || `Коробка №${equipment.packaging_box.id}`}`
        : 'В коробке';
    }
    return equipment.current_basket_info
      ? equipment.current_basket_info.display_name ||
          `${equipment.current_basket_info.basket_type} №${equipment.current_basket_info.number}`
      : equipment.repair_logs?.[0]?.performed_by || '-';
  };

  // Убрали if (!isOpen) return null; — теперь компонента всегда рендерится при isOpen=true,
  // но анимация управляется isAnimatingOpen. Для выхода держим в DOM до конца анимации.
  const panelContent = (
    <>
      {/* Overlay — используем isAnimatingOpen для класса open */}
      <div
        className={`${styles.historyOverlay} ${isAnimatingOpen ? styles.open : ''}`}
        onClick={handleClose}
      />
      
      {/* Panel — используем isAnimatingOpen для класса open */}
      <div className={`${styles.historyPanel} ${isAnimatingOpen ? styles.open : ''}`}>
        <div className={styles.historyHeader}>
          <div className={styles.historyTitle}>
            <History size={20} />
            <span>{title}</span>
            {equipmentHistory && (
              <span className={styles.serialBadge}>{equipmentHistory.serial_number}</span>
            )}
          </div>
          <button
            type="button"
            className={styles.closeHistoryButton}
            onClick={handleClose}
            aria-label="Закрыть"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className={styles.historyContent}>
          {loading ? (
            <div className={styles.historyLoading}>
              <div className={styles.spinner}></div>
              <p>Загрузка истории...</p>
            </div>
          ) : error ? (
            <div className={styles.historyError}>
              <AlertCircle size={24} />
              <p>{error}</p>
              <button className={styles.retryButton} onClick={fetchEquipmentHistory}>
                Повторить
              </button>
            </div>
          ) : equipmentHistory ? (
            <div className={styles.historyDetails}>
              {/* Equipment Info */}
              <div className={styles.historyCard}>
                <div className={styles.historyCardHeader}>
                  <h3>
                    <Wrench size={18} />
                    Информация об оборудовании
                  </h3>
                </div>
                <div className={styles.historyCardBody}>
                  <div className={styles.historyInfoGrid}>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <FileText size={16} />
                        Серийный номер
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.serial_number}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <Wrench size={16} />
                        Модель
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.equipment_model || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <AlertCircle size={16} />
                        Заявленная неисправность
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.declared_fault || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <Truck size={16} />
                        Статус перемещения
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.location_status_display || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <Wrench size={16} />
                        Статус ремонта
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_status_display || 'Не установлен'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <MapPin size={16} />
                        Местоположение
                      </span>
                      <span className={styles.historyInfoValue}>{getEquipmentLocation(equipmentHistory)}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <Calendar size={16} />
                        Начало ремонта
                      </span>
                      <span className={styles.historyInfoValue}>{formatDate(equipmentHistory.repair_start_date)}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <Calendar size={16} />
                        Окончание ремонта
                      </span>
                      <span className={styles.historyInfoValue}>{formatDate(equipmentHistory.repair_end_date)}</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Repair Request Info */}
              <div className={styles.historyCard}>
                <div className={styles.historyCardHeader}>
                  <h3>
                    <Building size={18} />
                    Информация о заявке
                  </h3>
                </div>
                <div className={styles.historyCardBody}>
                  <div className={styles.historyInfoGrid}>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <FileText size={16} />
                        Номер заявки
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.request_number}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <Building size={16} />
                        Заказчик
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.customer?.name || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <FileText size={16} />
                        ИНН заказчика
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.customer?.inn || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <Building size={16} />
                        Территория
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.territory?.tb_name || equipmentHistory.repair_request?.territory?.gosb_name || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <MapPin size={16} />
                        Город
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.territory?.city?.name || equipmentHistory.repair_request?.service_center?.city?.name || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <Wrench size={16} />
                        Сервисный центр
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.service_center?.name || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <MapPin size={16} />
                        Адрес
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.territory?.address || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <User size={16} />
                        Телефон
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.territory?.phone || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <Mail size={16} />
                        Email
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.territory?.email || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <AlertCircle size={16} />
                        Статус
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.status_display || '-'}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <Calendar size={16} />
                        Дата создания
                      </span>
                      <span className={styles.historyInfoValue}>{formatDate(equipmentHistory.repair_request?.created_at)}</span>
                    </div>
                    <div className={styles.historyInfoItem}>
                      <span className={styles.historyInfoLabel}>
                        <FileText size={16} />
                        Примечания
                      </span>
                      <span className={styles.historyInfoValue}>{equipmentHistory.repair_request?.notes || '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Approval States */}
              {equipmentHistory.repair_request?.approval_states?.length > 0 && (
                <div className={styles.historyCard}>
                  <div className={styles.historyCardHeader}>
                    <h3>
                      <CheckCircle size={18} />
                      Статус согласования
                    </h3>
                  </div>
                  <div className={styles.historyCardBody}>
                    <div className={styles.historyInfoGrid}>
                      {equipmentHistory.repair_request.approval_states.map((approvalState, index) => (
                        <React.Fragment key={index}>
                          <div className={styles.historyInfoItem}>
                            <span className={styles.historyInfoLabel}>Готово к согласованию</span>
                            <span className={styles.historyInfoValue}>
                              {approvalState.is_ready ?
                                <span className={styles.statusBadgeSuccess}>Да</span> :
                                <span className={styles.statusBadgeError}>Нет</span>}
                            </span>
                          </div>
                          <div className={styles.historyInfoItem}>
                            <span className={styles.historyInfoLabel}>Завершено</span>
                            <span className={styles.historyInfoValue}>
                              {approvalState.is_completed ?
                                <span className={styles.statusBadgeSuccess}>Да</span> :
                                <span className={styles.statusBadgeError}>Нет</span>}
                            </span>
                          </div>
                          <div className={styles.historyInfoItem}>
                            <span className={styles.historyInfoLabel}>Запросил</span>
                            <span className={styles.historyInfoValue}>{approvalState.requested_by || 'Не указано'}</span>
                          </div>
                          <div className={styles.historyInfoItem}>
                            <span className={styles.historyInfoLabel}>Создано</span>
                            <span className={styles.historyInfoValue}>{formatDate(approvalState.created_at) || 'Не указано'}</span>
                          </div>
                          <div className={styles.historyInfoItem}>
                            <span className={styles.historyInfoLabel}>Уведомлено о готовности</span>
                            <span className={styles.historyInfoValue}>{formatDate(approvalState.notified_ready_at) || 'Не указано'}</span>
                          </div>
                          <div className={styles.historyInfoItem}>
                            <span className={styles.historyInfoLabel}>Уведомлено о завершении</span>
                            <span className={styles.historyInfoValue}>{formatDate(approvalState.notified_complete_at) || 'Не указано'}</span>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {/* Custom Fields */}
              {equipmentHistory.custom_fields && Object.keys(equipmentHistory.custom_fields).length > 0 && (
                <div className={styles.historyCard}>
                  <div className={styles.historyCardHeader}>
                    <h3>
                      <FileText size={18} />
                      Дополнительные поля
                    </h3>
                  </div>
                  <div className={styles.historyCardBody}>
                    <div className={styles.historyInfoGrid}>
                      {Object.entries(equipmentHistory.custom_fields).map(([key, value]) => (
                        <div key={key} className={styles.historyInfoItem}>
                          <span className={styles.historyInfoLabel}>{key}</span>
                          <span className={styles.historyInfoValue}>{value || 'Не указано'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {/* Diagnostic and Repair Codes */}
              <div className={styles.historyCard}>
                <div className={styles.historyCardHeader}>
                  <h3>
                    <Wrench size={18} />
                    Коды диагностики и ремонта
                  </h3>
                </div>
                <div className={styles.historyCardBody}>
                  <div className={styles.codesSection}>
                    <h4 className={styles.codesTitle}>
                      <ShieldCheck size={16} />
                      Диагностические коды
                    </h4>
                    {equipmentHistory.repair_details?.diagnostic_codes?.length > 0 ? (
                      <ul className={styles.codesList}>
                        {equipmentHistory.repair_details.diagnostic_codes.map(dc => (
                          <li key={dc.id} className={styles.codeItem}>
                            <span className={styles.codeBadge}>[{dc.code}]</span>
                            <span className={styles.codeName}>{dc.name}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.noCodesMessage}>Нет данных</p>
                    )}
                  </div>
                  <div className={styles.codesSection}>
                    <h4 className={styles.codesTitle}>
                      <Wrench size={16} />
                      Ремонтные коды
                    </h4>
                    {equipmentHistory.repair_details?.repair_codes?.length > 0 ? (
                      <ul className={styles.codesList}>
                        {equipmentHistory.repair_details.repair_codes.map(rc => (
                          <li key={rc.id} className={styles.codeItem}>
                            <span className={styles.codeBadge}>[{rc.code}]</span>
                            <span className={styles.codeName}>{rc.name}</span>
                            <span className={styles.priceTag}>{parseFloat(rc.price).toLocaleString()} ₽</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.noCodesMessage}>Нет данных</p>
                    )}
                    <div className={styles.totalCost}>
                      <span className={styles.totalCostLabel}>Общая стоимость:</span>
                      <span className={styles.totalCostValue}>
                        {calculateTotalCost(equipmentHistory.repair_details?.repair_codes).toLocaleString()} ₽
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Media Files */}
              {(equipmentHistory.photos?.length > 0 || equipmentHistory.notes?.length > 0) && (
                <div className={styles.historyCard}>
                  <div className={styles.historyCardHeader}>
                    <h3>
                      <FileText size={18} />
                      Файлы и заметки
                    </h3>
                  </div>
                  <div className={styles.historyCardBody}>
                    <div className={styles.mediaButtons}>
                      {equipmentHistory.photos?.length > 0 && (
                        <button
                          className={styles.mediaButton}
                          onClick={() => {
                            setCurrentPhotos(equipmentHistory.photos);
                            setIsPhotoViewerOpen(true);
                          }}
                        >
                          <ImageIcon size={16} />
                          Просмотреть фото ({equipmentHistory.photos.length})
                        </button>
                      )}
                      {equipmentHistory.notes?.length > 0 && (
                        <button
                          className={styles.mediaButton}
                          onClick={() => {
                            setCurrentNotes(equipmentHistory.notes);
                            setIsNotesViewerOpen(true);
                          }}
                        >
                          <FileText size={16} />
                          Просмотреть заметки ({equipmentHistory.notes.length})
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* Action Log */}
              <div className={styles.historyCard}>
                <div className={styles.historyCardHeader}>
                  <h3>
                    <History size={18} />
                    Журнал действий
                  </h3>
                </div>
                <div className={styles.historyCardBody}>
                  {equipmentHistory.repair_logs?.length > 0 ? (
                    <ul className={styles.historyLogList}>
                      {equipmentHistory.repair_logs
                        .slice()
                        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                        .map((log, idx) => {
                          const noteText = log.note_display || log.note;
                          return (
                            <li key={log.id || idx} className={styles.historyLogItem}>
                              <div className={styles.historyLogHeader}>
                                <span className={styles.historyLogAction}>{log.action}</span>
                                <span className={styles.historyLogTime}>
                                  {new Date(log.timestamp).toLocaleString()}
                                </span>
                              </div>
                              <div className={styles.historyLogDetails}>
                                <span className={styles.historyLogUser}>{log.performed_by || 'Не указано'}</span>
                                {noteText && <p className={styles.historyLogNote}>{noteText}</p>}
                              </div>
                            </li>
                          );
                        })}
                    </ul>
                  ) : (
                    <p className={styles.noLogsMessage}>Нет информации о действиях.</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.historyEmpty}>
              <p>Выберите оборудование для просмотра истории</p>
            </div>
          )}
        </div>
      </div>
      {/* Photo and Notes Modals */}
      <PhotoViewerModal
        photos={currentPhotos}
        isOpen={isPhotoViewerOpen}
        onClose={() => setIsPhotoViewerOpen(false)}
      />
      <NotesViewerModal
        notes={currentNotes}
        isOpen={isNotesViewerOpen}
        onClose={() => setIsNotesViewerOpen(false)}
      />
    </>
  );

  // The history drawer is often opened from another fixed drawer. Rendering it
  // inside that drawer traps it in the parent's stacking context, so no z-index
  // can place it above the request header. A body portal gives it an independent
  // top-level layer while preserving the same React state and event handlers.
  return typeof document !== 'undefined'
    ? createPortal(panelContent, document.body)
    : panelContent;
};

export default EquipmentHistoryPanel;
